import React from 'react';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core';
import { API_URL } from '../../constants/api';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import { apiBatchRequest, apiPost, apiRequest } from '../../utils/api_service';
import SaveIcon from '@material-ui/icons/Save';
import { ShowToast, toastType } from '../../utils/toast-service';
import { isGridFormValid, IsSectionVisible, UpdateColumnWithConfiguration } from '../../utils/helper';
import GridTable from '../common/Table/Table';
import { SECTIONS } from '../../constants/appConstants';
import { expenseGroupMappingColumnList } from '../../constants/MockData';
import _ from 'lodash';

// Component to Configure Pay codes with Expense Groups
class ConfigureExpenseGroupAndPayCodes extends React.Component {
  // Constructor to set initial state
  constructor (props) {
    super(props);
    this.state = {
      expenseGroupMasterData: [],
      paycodeTypeMasterData: [],
      configuration: [],
      expenseGroupsAndPayCodesMappingData: [],
      isInAddMode: false,
      selectedEditRowId: undefined,
      updateData: {
        expenseGroupPayCodeId: 0,
        expenseGroupId: 0,
        expenseGroup: null,
        payCodeId: null,
        paycodeName: null,
        paycodeApplicableForId: null,
        paycodeApplicableFor: null
      },
      paycodeApplicableForSourceData: 
      [ {paycodeApplicableForId: 'Market', paycodeApplicableFor: 'Market'},
        {paycodeApplicableForId: 'Property', paycodeApplicableFor: 'Property'},
        {paycodeApplicableForId: 'Both', paycodeApplicableFor: 'Both'}
      ],
      expenseGroupMappingColumn: _.cloneDeep(expenseGroupMappingColumnList)
    };
  }

  // Method executes before components are rendered, to get all required data from database
  componentDidMount () {
      this.getRequiredData();      
  }

  // Get required master data and Mapping data of pay codes with Expense groups
  getRequiredData = () => {
    this._asyncRequest = apiBatchRequest([
      {
        url: API_URL.Report.GetExpenseGroup,
        key: 'expenseGroupMasterData',
        fromCache: true,
        duration: 15
      },
      {
        url: API_URL.Users.GetPaycodeType,
        key: 'paycodeTypeMasterData',
        fromCache: true,
        duration: 15
      },
      {
        url: API_URL.Users.GetExpenseGroupPayCodeMapping,
        key: 'expenseGroupsAndPayCodesMapping'
      }
    ])
      .then(({ expenseGroupMasterData, paycodeTypeMasterData, expenseGroupsAndPayCodesMapping }) => {
        this._asyncRequest = null;
        if (expenseGroupsAndPayCodesMapping.success !== true) {
          ShowToast('Something went wrong', toastType.ERROR, 5000, 500, 'Server error');
        }
        
        this.setState({
          expenseGroupMasterData: expenseGroupMasterData.data || [],
          paycodeTypeMasterData: paycodeTypeMasterData.data || [],
          expenseGroupsAndPayCodesMappingData: expenseGroupsAndPayCodesMapping.data || [],
          configuration: expenseGroupsAndPayCodesMapping.config || []
        });
      })
      .catch(() => {
        this._asyncRequest = null;
      });
  };

  // Get mapping data of Expense groups and pay codes
  GetExpenseGroupPayCodeMapping = () => {
    apiRequest({
      url: API_URL.Users.GetExpenseGroupPayCodeMapping
    }).then(({ data: response }) => {
      this.setState({
        expenseGroupsAndPayCodesMappingData: response || []
      });
    });
  };

  // Cancel add/edit mode
  cancelMode = () => {
    this.setState({ selectedEditRowId: undefined, isInAddMode: false, updateData: undefined });
    this.GetExpenseGroupPayCodeMapping();
  };

  // Check Validation on Form fields
  isValidForm = () => {
    let IsValid = true;
    const { updateData, expenseGroupMappingColumn } = this.state;
    expenseGroupMappingColumn.forEach((ele) => {
      isGridFormValid(updateData[ele.key], ele, updateData);
      if (ele.isInErrorState === true) {
        IsValid = false;
      }
    });
    this.setState({ expenseGroupMappingColumn }, this.forceUpdate());
    return IsValid;
  };

  // Add/Update mapping record
  saveUpdatedRecord = () => {
    if (!this.isValidForm()) { return false; }
    const newParam = this.state.updateData;
    const params = {
      payCodeId: newParam.payCodeId,
      payCodeTypeId: newParam.payCodeTypeId,
      expenseGroupId: newParam.expenseGroupId,
      subjectToBurdenRate: newParam.subjectToBurdenRate,
      paycodeApplicableFor: newParam.paycodeApplicableFor
    };

    apiPost({
      url: API_URL.Users.UpdatePayCodeDetail,
      params: {},
      postBody: params
    }).then(({ data: response }) => {
      if (response.success === true) {
        ShowToast(this.state.isInAddMode ? 'Record added successfully.' : 'Record updated successfully.', toastType.SUCCESS, 5000, 500);
        this.setState({
          updateData: undefined,
          selectedEditRowId: undefined
        });
        this.GetExpenseGroupPayCodeMapping();
      } else {
        ShowToast(response.message, toastType.ERROR, 5000, 500);
      }
    });
  };

  // Edit selected record
  EditRecord = (cellInfo) => {
    this.setState({ selectedEditRowId: { key: 'payCodeId', value: cellInfo.payCodeId }, isInAddMode: false, updateData: { ...cellInfo }, expenseGroupMappingColumn: _.cloneDeep(expenseGroupMappingColumnList) });
  }

  // Render Edit/Update action columns on each records based on conditions
  renderActionColumn = (cellInfo) => {
    return (
      <>
        {this.state.selectedEditRowId !== undefined && cellInfo.payCodeId === this.state.selectedEditRowId.value
          ? (
            <>
              <Tooltip title="Save Record">
                <IconButton aria-label={'refreshData'} size="small" onClick={() => { }}>
                  <SaveIcon fontSize="small" size="small" onClick={this.saveUpdatedRecord} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton aria-label={'refreshData'} size="small" onClick={this.cancelMode}>
                  <CancelIcon fontSize="small" size="small" />
                </IconButton>
              </Tooltip>
            </>
            )
          : (IsSectionVisible(SECTIONS.CONFIGURATION, SECTIONS.CONFIGURE_EXPENSE_GROUP_PAYCODE, SECTIONS.ADD_EDIT)) && (
            <Tooltip title="Edit Record">
              <IconButton
                aria-label={'refreshData'}
                size="small"
                onClick={() => {
                  this.EditRecord(cellInfo);
                }}
              >
                <EditIcon fontSize="small" size="small" />
              </IconButton>
            </Tooltip>
            )}
      </>
    );
  };

  // Handle edit event of row
  handleTableEditChange = (value, fieldName, row, addMode) => {
    const { expenseGroupMappingColumn, paycodeTypeMasterData, expenseGroupMasterData, paycodeApplicableForSourceData } = this.state;
    const param = { ...this.state.updateData };
    
    if (fieldName === 'expenseGroup') {
      param.expenseGroupId = expenseGroupMasterData.find(x=> x.expenseGroup === value)?.expenseGroupId;
      param[fieldName] = value;
    } else if (fieldName === 'payCodeType') {
      param.payCodeTypeId = paycodeTypeMasterData.find(x=> x.payCodeType === value)?.payCodeTypeId;
      param[fieldName] = value;
    } else if (fieldName === 'paycodeApplicableFor') {
      param.paycodeApplicableForId = paycodeApplicableForSourceData.find(x=> x.paycodeApplicableFor === value)?.paycodeApplicableForId;
      param[fieldName] = value;
    } else {
      param[fieldName] = value;
    }

    // Check validations on every field
    isGridFormValid(value, expenseGroupMappingColumn.find(x => x.key === fieldName), param);
    this.setState({ updateData: param, reloadTable: !this.state.reloadTable });
  };

  // Add new record of Expense Group and Pay codes mapping
  AddNewRecord = () => {
    if (this.state.isInAddMode === true) {
      ShowToast('You can add one row at a time!', toastType.WARNING, 5000, 500);
      return false;
    }

    // Object of new record
    const rowNew = {
      payCodeId: 0,
      payCode: null,
      streamlinePayCode: null,
      payCodeTypeId: 0,
      payCodeType: null,
      expenseGroupId: 0,
      expenseGroup: null
    };
    this.setState({ isInAddMode: true, selectedEditRowId: { key: 'expenseGroupId', value: 0 }, updateData: rowNew });
  };

  // Render components based on conditions
  render () {
    const { paycodeTypeMasterData, expenseGroupMasterData, expenseGroupMappingColumn, expenseGroupsAndPayCodesMappingData, paycodeApplicableForSourceData } = this.state;
    const EditSource = [];
    EditSource.push(
      { key: 'expenseGroupMasterData', data: expenseGroupMasterData },
      { key: 'paycodeTypeMasterData', data: paycodeTypeMasterData },
      { key: 'paycodeApplicableForSourceData', data: paycodeApplicableForSourceData }
    );
    return (
      <>
        <Table
          aria-labelledby="tableMenuTitle"
          size={'small'}
          aria-label="configuration table"
        >
          <TableHead>
            <TableRow>
              <TableCell
                key={'configureHeader'}
                className="content-header"
                colSpan={2}
              >
                <div className="content-header d-flex">
                  {' '}
                  <div
                    className="col-xs-11"
                    style={{ display: 'inline-block', alignItems: 'center' }}
                  >
                    Expense Group And Pay Codes Configuration
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <GridTable
                  columns={UpdateColumnWithConfiguration(expenseGroupMappingColumn, this.state.configuration, [])}
                  data={expenseGroupsAndPayCodesMappingData}
                  config={expenseGroupMappingColumn}
                  renderActionColumn={this.renderActionColumn}
                  selectedEditRowId={this.state.selectedEditRowId}
                  isInAddMode={this.state.isInAddMode}
                  EditSource={EditSource}
                  handleTableEditChange={this.handleTableEditChange}
                  updateData={this.state.updateData}
                  reload={this.state.reloadTable}
                  isInline={true}
                ></GridTable>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </>
    );
  };
}

export default ConfigureExpenseGroupAndPayCodes;